import { useEffect, useMemo, useRef, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout';
import type { CartItem } from '../../store/usePosStore';
import type { CartEvaluationResult } from '../../../promotions/public-api';
import {
  buildEvaluatedLineMap,
  formatPromoMoney,
  summarizeCheckoutDiscounts,
} from '../../../promotions/public-api';
import { POS_MOBILE_CART_COMPACT_LAYOUT } from '../../application/posLayout.constants';

export type UsePosCartSectionLayoutInput = {
  cart: CartItem[];
  discountTotal?: number;
  cartEvaluation?: CartEvaluationResult | null;
  inSheet: boolean;
  linesScrollMaxHeightProp?: number;
};

export type UsePosCartSectionLayoutResult = {
  isCompactCart: boolean;
  useMobileCartCompact: boolean;
  useCompactRows: boolean;
  cartContentWidth: number;
  setCartContentWidth: (w: number) => void;
  cartLinesExpanded: boolean;
  setCartLinesExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  showCartCollapse: boolean;
  cartLinesCollapsed: boolean;
  linesScrollMaxHeight: number;
  evaluatedByLineId: ReturnType<typeof buildEvaluatedLineMap>;
  itemCountLabel: string;
  cartBadgePulse: boolean;
  savingsLabel: string | null;
  /** Una fila por promo aplicada (monto + nombre). */
  promoSavingsLines: string[];
  hasDiscounts: boolean;
};

export function usePosCartSectionLayout({
  cart,
  discountTotal,
  cartEvaluation,
  inSheet,
  linesScrollMaxHeightProp,
}: UsePosCartSectionLayoutInput): UsePosCartSectionLayoutResult {
  const { height } = useWindowDimensions();
  const { layoutWide: isWideLayout } = useTabWorkspaceLayout();
  const isCompactCart = !isWideLayout;
  const useMobileCartCompact = isCompactCart && POS_MOBILE_CART_COMPACT_LAYOUT && !inSheet;
  const [cartContentWidth, setCartContentWidth] = useState(0);
  const useCompactRows = (isCompactCart && !inSheet) || cartContentWidth < 420;
  const [cartLinesExpanded, setCartLinesExpanded] = useState(inSheet || !useMobileCartCompact);
  const showCartCollapse = inSheet
    ? false
    : useMobileCartCompact
      ? cart.length >= 1
      : isCompactCart && cart.length > 4;
  const cartLinesCollapsed = showCartCollapse && !cartLinesExpanded;
  const evaluatedByLineId = useMemo(() => buildEvaluatedLineMap(cartEvaluation), [cartEvaluation]);

  const hasDiscounts = discountTotal != null && discountTotal > 0;

  const promoSavingsLines = useMemo(() => {
    if (!cartEvaluation || !hasDiscounts || discountTotal == null || discountTotal <= 0) {
      return [];
    }
    const rows = summarizeCheckoutDiscounts(cartEvaluation);
    if (rows.length === 0) {
      return [`−${formatPromoMoney(discountTotal).slice(1)}`];
    }
    const byPromo = new Map<string, { name: string; amount: number }>();
    for (const row of rows) {
      const prev = byPromo.get(row.promotionId);
      if (prev) {
        prev.amount += row.discountAmount;
      } else {
        byPromo.set(row.promotionId, {
          name: row.promotionName,
          amount: row.discountAmount,
        });
      }
    }
    return [...byPromo.values()].map(
      (row) => `−${formatPromoMoney(row.amount).slice(1)} · ${row.name}`,
    );
  }, [cartEvaluation, discountTotal, hasDiscounts]);

  const savingsLabel = useMemo(() => {
    if (!hasDiscounts || discountTotal == null) return null;
    const amount = formatPromoMoney(discountTotal);
    if (promoSavingsLines.length === 1) return `${amount} · ${promoSavingsLines[0]!.split(' · ')[1] ?? 'promo'}`;
    if (promoSavingsLines.length > 1) {
      return `${amount} · ${promoSavingsLines.length} promos`;
    }
    return amount;
  }, [discountTotal, hasDiscounts, promoSavingsLines]);

  const linesScrollMaxHeight = useMemo(() => {
    if (linesScrollMaxHeightProp != null) {
      return linesScrollMaxHeightProp;
    }
    if (isWideLayout) {
      return Math.min(height * 0.46, 420);
    }
    if (inSheet) {
      return Math.min(Math.max(height * 0.42, 160), 360);
    }
    if (POS_MOBILE_CART_COMPACT_LAYOUT) {
      return Math.min(Math.max(height * 0.22, 100), 180);
    }
    return Math.min(Math.max(height * 0.34, 160), 280);
  }, [height, inSheet, isWideLayout, linesScrollMaxHeightProp]);

  const itemCountLabel = cart.length === 1 ? '1 ítem' : `${cart.length} ítems`;
  const prevCartCountRef = useRef(cart.length);
  const [cartBadgePulse, setCartBadgePulse] = useState(false);

  useEffect(() => {
    if (cart.length > prevCartCountRef.current) {
      setCartBadgePulse(true);
      const timer = setTimeout(() => setCartBadgePulse(false), 520);
      prevCartCountRef.current = cart.length;
      return () => clearTimeout(timer);
    }
    prevCartCountRef.current = cart.length;
  }, [cart.length]);

  return {
    isCompactCart,
    useMobileCartCompact,
    useCompactRows,
    cartContentWidth,
    setCartContentWidth,
    cartLinesExpanded,
    setCartLinesExpanded,
    showCartCollapse,
    cartLinesCollapsed,
    linesScrollMaxHeight,
    evaluatedByLineId,
    itemCountLabel,
    cartBadgePulse,
    savingsLabel,
    promoSavingsLines,
    hasDiscounts,
  };
}
